interface FlourishProps {
  className?: string;
  ariaLabel?: string;
}

/**
 * Small block-print mango-paisley motif. Hand-drawn-feeling SVG with one
 * teardrop + a seed. Pulls from traditional Indian textile printing — used
 * in place of clean horizontal rules.
 */
export function Flourish({ className, ariaLabel }: FlourishProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role={ariaLabel ? "img" : "presentation"}
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
    >
      <path
        d="M7 23 C 6 12, 18 5, 24 11 C 28 15, 26 22, 19 24 C 13 25, 9 25, 7 23 Z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <circle cx="16" cy="18" r="1.3" fill="currentColor" />
      <path
        d="M11 19 C 12 16, 14 14, 18 13"
        stroke="currentColor"
        strokeWidth="0.9"
        strokeLinecap="round"
        opacity="0.55"
        fill="none"
      />
    </svg>
  );
}

/**
 * Decorative horizontal divider — a thin line on each side of the Flourish,
 * used in section headings as a tactile alternative to a clean saffron rule.
 */
export function FlourishDivider({ className }: { className?: string }) {
  return (
    <div
      className={`flex items-center justify-center gap-4 ${className ?? ""}`}
    >
      <span className="block h-px w-12 bg-saffron-dark/40" />
      <Flourish className="h-7 w-7 text-saffron-dark/70" />
      <span className="block h-px w-12 bg-saffron-dark/40" />
    </div>
  );
}

/**
 * SectionThread — vertical visual link between sections, like the seam
 * of a textile threading the page from top to bottom. A single decorative
 * element used at section boundaries to keep the rhythm intentional.
 */
export function SectionThread({
  variant = "default",
  className,
}: {
  variant?: "default" | "dark";
  className?: string;
}) {
  const lineClass =
    variant === "dark" ? "bg-saffron-light/40" : "bg-saffron-dark/35";
  const motifClass =
    variant === "dark" ? "text-saffron-light/70" : "text-saffron-dark/60";
  return (
    <div
      className={`flex justify-center bg-transparent py-10 ${className ?? ""}`}
      aria-hidden="true"
    >
      <div className="flex flex-col items-center gap-3">
        <span className={`block h-12 w-px ${lineClass}`} />
        <Flourish className={`h-3 w-3 ${motifClass}`} />
        <span
          className={`block h-2 w-2 rotate-45 border ${
            variant === "dark"
              ? "border-saffron-light/55"
              : "border-saffron-dark/45"
          }`}
        />
        <Flourish className={`h-3 w-3 ${motifClass}`} />
        <span className={`block h-12 w-px ${lineClass}`} />
      </div>
    </div>
  );
}
